<template>
  <div class="app-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span><i class="el-icon-setting" /> Paramètres du Tenant</span>
      </div>

      <el-tabs v-model="activeTab">
        <el-tab-pane label="Intégrations" name="integrations">
          <div class="integration-section">
            <h3><i class="el-icon-connection" /> Clé d'API (Intégration Dolibarr)</h3>
            <p class="description">
              Cette clé permet d'authentifier les appels de l'API AAS provenant du module Dolibarr <code>aaslink</code>.
            </p>

            <el-alert
              title="Information de sécurité"
              type="info"
              description="Ne partagez pas cette clé. Si vous pensez qu'elle a été compromise, régénérez-la immédiatement."
              show-icon
              :closable="false"
              style="margin-bottom: 20px;"
            />

            <div class="api-key-container">
              <el-form label-position="top">
                <el-form-item label="URL de l'API Dolibarr SAU">
                  <el-input
                    v-model="dolibarrUrl"
                    placeholder="Ex: http://votre-dolibarr.com/custom/aaslink/api"
                  />
                  <p class="help-text">URL pointant vers le dossier API du module <code>aaslink</code> dans Dolibarr SAU.</p>
                </el-form-item>
                <el-form-item label="URL de l'API Dolibarr TRADING">
                  <el-input
                    v-model="dolibarrUrlTrading"
                    placeholder="Ex: http://votre-dolibarr.com/custom/aaslink/api"
                  />
                  <p class="help-text">URL pointant vers le dossier API du module <code>aaslink</code> dans Dolibarr TRADING.</p>
                </el-form-item>
                <el-form-item label="Clé d'API actuelle">
                  <div class="key-display">
                    <el-input
                      v-model="apiKey"
                      readonly
                      placeholder="Aucune clé générée"
                      class="api-key-input"
                    >
                      <template slot="append">
                        <el-button v-clipboard:copy="apiKey" v-clipboard:success="onCopySuccess" icon="el-icon-document-copy">Copier</el-button>
                      </template>
                    </el-input>
                  </div>
                </el-form-item>

                <el-form-item>
                  <el-button type="success" :loading="saving" @click="handleSaveSettings">
                    <i class="el-icon-check" /> Enregistrer les paramètres
                  </el-button>
                  <el-button type="primary" :loading="loading" @click="handleGenerateKey">
                    <i class="el-icon-refresh" /> {{ apiKey ? 'Régénérer la clé' : 'Générer une clé' }}
                  </el-button>
                </el-form-item>
              </el-form>
            </div>

            <div v-if="apiKey" class="documentation-section">
              <h4>Comment utiliser cette clé ?</h4>
              <p>Dans la configuration du module <code>aaslink</code> sur Dolibarr :</p>
              <ul>
                <li><strong>AasApiKey</strong> : Copiez la clé ci-dessus.</li>
                <li><strong>AasApiUrl</strong> : <code>{{ apiUrl }}</code></li>
              </ul>
              <p>Header HTTP requis pour les appels directs : <code>X-API-KEY: {{ apiKey }}</code></p>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane v-if="checkRole(['admin'])" label="Mobile / Campagnes" name="mobile-campagnes">
          <div class="integration-section">
            <h3><i class="el-icon-mobile-phone" /> {{ $t('tenantSettings.mobileCampagnes.title') }}</h3>
            <p class="description">
              {{ $t('tenantSettings.mobileCampagnes.description') }}
            </p>

            <el-alert
              :title="$t('tenantSettings.mobileCampagnes.helpTitle')"
              type="info"
              :description="$t('tenantSettings.mobileCampagnes.helpText')"
              show-icon
              :closable="false"
              style="margin-bottom: 20px;"
            />

            <el-form v-loading="policiesLoading" label-position="top" class="api-key-container">
              <el-form-item :label="$t('tenantSettings.mobileCampagnes.allowFinishCollecte')">
                <el-switch v-model="campagnePolicies.closed_campaign.allow_finish_collecte" />
              </el-form-item>
              <el-form-item :label="$t('tenantSettings.mobileCampagnes.allowAddSacs')">
                <el-switch v-model="campagnePolicies.closed_campaign.allow_add_sacs" />
              </el-form-item>
              <el-form-item :label="$t('tenantSettings.mobileCampagnes.allowNewCi')">
                <el-switch v-model="campagnePolicies.closed_campaign.allow_new_ci" />
              </el-form-item>
              <el-form-item :label="$t('tenantSettings.mobileCampagnes.allowNewParcelle')">
                <el-switch v-model="campagnePolicies.closed_campaign.allow_new_parcelle" />
              </el-form-item>
              <el-form-item :label="$t('tenantSettings.mobileCampagnes.allowNewFormation')">
                <el-switch v-model="campagnePolicies.closed_campaign.allow_new_formation" />
              </el-form-item>
              <el-form-item :label="$t('tenantSettings.mobileCampagnes.allowEditParcelleGps')">
                <el-switch v-model="campagnePolicies.closed_campaign.allow_edit_parcelle_gps" />
              </el-form-item>
              <el-form-item :label="$t('tenantSettings.mobileCampagnes.allowEditParcelleInfo')">
                <el-switch v-model="campagnePolicies.closed_campaign.allow_edit_parcelle_info" />
              </el-form-item>

              <el-form-item>
                <el-button type="primary" :loading="policiesSaving" @click="handleSaveCampagnePolicies">
                  <i class="el-icon-check" /> {{ $t('tenantSettings.mobileCampagnes.save') }}
                </el-button>
              </el-form-item>
            </el-form>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script>
import request from '@/utils/request';
import checkRole from '@/utils/role';

const defaultCampagnePolicies = () => ({
  closed_campaign: {
    allow_finish_collecte: true,
    allow_add_sacs: true,
    allow_new_ci: false,
    allow_new_parcelle: false,
    allow_new_formation: false,
    allow_edit_parcelle_gps: true,
    allow_edit_parcelle_info: false,
  },
});

export default {
  name: 'TenantSettings',
  data() {
    return {
      activeTab: 'integrations',
      apiKey: '',
      dolibarrUrl: '',
      dolibarrUrlTrading: '',
      loading: false,
      saving: false,
      policiesLoading: false,
      policiesSaving: false,
      campagnePolicies: defaultCampagnePolicies(),
    };
  },
  computed: {
    apiUrl() {
      // Retourne l'URL de base de l'API
      return process.env.VUE_APP_BASE_API || window.location.origin + '/api';
    },
  },
  created() {
    this.fetchSettings();
    if (checkRole(['admin'])) {
      this.fetchCampagnePolicies();
    }
  },
  methods: {
    checkRole,
    async fetchSettings() {
      try {
        const response = await request({
          url: '/settings/api-key',
          method: 'get',
        });
        if (response.success) {
          this.apiKey = response.data.api_key;
          this.dolibarrUrl = response.data.dolibarr_url || '';
          this.dolibarrUrlTrading = response.data.dolibarr_url_trading || '';
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des paramètres:', error);
      }
    },
    async handleSaveSettings() {
      this.saving = true;
      try {
        const response = await request({
          url: '/settings/update-settings',
          method: 'post',
          data: {
            dolibarr_url: this.dolibarrUrl,
            dolibarr_url_trading: this.dolibarrUrlTrading,
          },
        });

        if (response.success) {
          this.$message({
            type: 'success',
            message: 'Paramètres mis à jour avec succès.',
          });
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        this.$message({
          type: 'error',
          message: 'Erreur lors de la mise à jour des paramètres.',
        });
      } finally {
        this.saving = false;
      }
    },
    async handleGenerateKey() {
      const confirmMessage = this.apiKey
        ? 'Êtes-vous sûr de vouloir régénérer la clé ? L\'ancienne clé cessera de fonctionner immédiatement.'
        : 'Voulez-vous générer une clé d\'API pour ce tenant ?';

      try {
        await this.$confirm(confirmMessage, 'Confirmation', {
          confirmButtonText: 'Confirmer',
          cancelButtonText: 'Annuler',
          type: 'warning',
        });

        this.loading = true;
        const response = await request({
          url: '/settings/api-key/generate',
          method: 'post',
        });

        if (response.success) {
          this.apiKey = response.data.api_key;
          this.$message({
            type: 'success',
            message: 'La clé d\'API a été générée avec succès.',
          });
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message({
            type: 'error',
            message: 'Erreur lors de la génération de la clé.',
          });
        }
      } finally {
        this.loading = false;
      }
    },
    onCopySuccess() {
      this.$message({
        type: 'success',
        message: 'Clé copiée dans le presse-papier !',
      });
    },
    async fetchCampagnePolicies() {
      this.policiesLoading = true;
      try {
        const response = await request({
          url: '/settings/campagne-policies',
          method: 'get',
        });
        if (response.success && response.data) {
          this.campagnePolicies = {
            ...defaultCampagnePolicies(),
            ...response.data,
            closed_campaign: {
              ...defaultCampagnePolicies().closed_campaign,
              ...(response.data.closed_campaign || {}),
            },
          };
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des policies campagne:', error);
      } finally {
        this.policiesLoading = false;
      }
    },
    async handleSaveCampagnePolicies() {
      this.policiesSaving = true;
      try {
        const response = await request({
          url: '/settings/campagne-policies',
          method: 'put',
          data: this.campagnePolicies,
        });

        if (response.success) {
          this.campagnePolicies = response.data || this.campagnePolicies;
          this.$message({
            type: 'success',
            message: this.$t('tenantSettings.mobileCampagnes.saved'),
          });
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des policies:', error);
        this.$message({
          type: 'error',
          message: this.$t('tenantSettings.mobileCampagnes.saveError'),
        });
      } finally {
        this.policiesSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}
.clearfix:after {
  clear: both;
}

.integration-section {
  padding: 10px;
}

.description {
  color: #606266;
  margin-bottom: 20px;
}

.api-key-container {
  max-width: 600px;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}

.api-key-input {
  font-family: monospace;
}

.documentation-section {
  margin-top: 30px;
  padding: 20px;
  background: #f0f9eb;
  border-radius: 4px;
  border: 1px solid #e1f3d8;
}

.documentation-section h4 {
  margin-top: 0;
  color: #67c23a;
}

code {
  background: #eef1f6;
  padding: 2px 4px;
  border-radius: 2px;
  color: #f56c6c;
}
</style>
